/**
 * @namespace Utils
 * @category Utils
 * @module Async
 * @description Async helpers
 * */

import { uniqueIdFromString } from './crypto'

/**

Throttles a function by wrapping it in a debounce. Ensures the function
only executes at most once per every wait milliseconds.
@param {Function} fn - The function to throttle
@param {number} wait - The threshold in ms to limit execution
@returns {Function} The throttled function with debounced execution
*/
export function throttle(fn, wait = 500) {
  let timer = null

  /**
   * @returns {Boolean} Whenever the execution will take place or not
   */
  return function () {
    const context = this
    const args = arguments

    if (!timer) {
      timer = setTimeout(() => {
        let r = fn.apply(context, args)
        timer = null
      }, wait)
      return true
    } else {
      return false
    }
  }
}

export function throttleSimple(fn, wait = 500) {
  let timer = null
  return function () {
    clearTimeout(timer) //Clear scheduled execution if any
    timer = setTimeout(() => {
      fn.apply(this, arguments)
      timer = null
    }, wait)
  }
}

/**
 * Throttle function
 *
 * - If not processing, schudule (1)
 * - If not precessing but scheduled, replace (2)
 * - If procesing, queue next (3)
 *
 * @param {function} fn Handler to throttle
 * @param {number} wait Cooldown time
 * @returns
 */
export function throttleSmart(fn, wait = 500) {
  let timerAt = null
  let timer = null
  let promise = null
  let processing = false
  let previousResolve = null
  let id = uniqueIdFromString(fn.toString())
  const timeoutHandler = async (context, args) => {
    const r = fn.apply(context, args)
    if (r instanceof Promise) {
      await r
    }
  }

  /**
   * @returns {void}
   */
  return function () {
    //console.debugVerboseScope(8,'async', 'throttleSmart start', id)
    return new Promise((resolve, reject) => {
      const context = this
      const args = arguments
      const schedule = () => {
        previousResolve = () => {
          /*console.debugVerboseScope(
            'async',
            '1',
            'throttleSmart resolve last',
            id
          )*/
          resolve()
        }
        //If already scheduled for 300ms and wait is 500ms, re-schedule for 200ms only.
        let currWait =
          timerAt === null
            ? wait
            : Date.now() - timerAt < wait
            ? wait - (Date.now() - timerAt)
            : wait
        timerAt = Date.now()
        timer = setTimeout(() => {
          console.debugVerboseScope(8, 'async', 'throttleSmart running', id)
          processing = true
          promise = timeoutHandler(context, args)
          promise
            .finally(() => {
              processing = false
              console.debugVerboseScope(
                '1',
                'async',
                'throttleSmart resolved',
                id,
                'timeoutDuration',
                currWait
              )
              clearTimeout(timer)
              timer = null
            })
            .then(resolve)
            .catch((e) => {
              console.error('throttleSmart error', e)
              reject(e)
            })
        }, currWait)
      }

      //case 1: normal schedule
      if (!timer) {
        console.debugVerboseScope(
          '1',
          'async',
          'throttleSmart schedule (1)',
          id
        )
        schedule()
      } else {
        if (!processing) {
          if (previousResolve) {
            clearTimeout(timer) //Clear previous execution plan
            previousResolve()
            previousResolve = null
          } else {
            throw new Error('Previous resolver not found')
          }
          console.debugVerboseScope(
            '1',
            'async',
            'throttleSmart schedule replace (2)',
            id
          )
          schedule()
        } else {
          console.debugVerboseScope(
            '1',
            'async',
            'throttleSmart queue next (3)',
            id
          )
          promise.then(schedule).catch(reject) //Wait previous execution and schedule
        }
      }
    })
  }
}
